import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  BodyTranscribeAndSummarizeAudioApiV1TranscriptionSummarizePost,
  TranscriptQueryRequestSchema,
} from "@/api/generated";
import {
  confirmRecordingConsentApiV1TranscriptionConsentPost,
  queryTranscriptApiV1TranscriptionQueryTranscriptPost,
  retryTranscriptionSummaryApiV1TranscriptionSummarizeRetryPost,
  transcribeAndSummarizeAudioApiV1TranscriptionSummarizePost,
} from "@/api/generated/sdk.gen";
import { CLIENT_KEYS } from "@/constants/clientKeys";
import { buildApiErrorMessage } from "@/utils/notification";

/**
 * Hook to confirm Assessment Scribe recording consent for a case (SCRIBE-02).
 * Must succeed before a live recording session is started — see
 * AudioRecorder.tsx's `handleStart`.
 */
export const useConfirmRecordingConsent = () => {
  return useMutation({
    mutationFn: async (caseNumber: string) => {
      const response = await confirmRecordingConsentApiV1TranscriptionConsentPost({
        client: apiClient,
        body: { case_number: caseNumber },
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to confirm recording consent", response.error)
        );
      }

      return response.data;
    },
  });
};

/**
 * Re-run a case's transcription from the recording the server kept (MDRS-575).
 * Refreshing the assessment is enough to follow it: the Scribe page resumes
 * polling whenever the assessment is TRANSCRIBING with a task id.
 */
export const useRetryTranscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (caseNumber: string) => {
      const response =
        await retryTranscriptionSummaryApiV1TranscriptionSummarizeRetryPost({
          client: apiClient,
          body: { case_number: caseNumber },
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Couldn't retry the transcription", response.error)
        );
      }

      return response.data;
    },
    // On failure too: a stale view (say, it's already transcribing) would
    // otherwise keep offering a retry the server refuses. Returned so the
    // mutation stays pending until the refetch lands — otherwise the banner
    // briefly accepts another click on the stale view.
    onSettled: (_data, _error, caseNumber) =>
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
      }),
  });
};

/**
 * Hook for querying transcripts
 * @returns Mutation for querying transcripts
 */
export const useQueryTranscript = () => {
  return useMutation({
    mutationFn: async (data: TranscriptQueryRequestSchema) => {
      const response = await queryTranscriptApiV1TranscriptionQueryTranscriptPost({
        client: apiClient,
        body: data,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to query transcript", response.error)
        );
      }

      return response.data;
    },
  });
};

/**
 * Hook to start transcription + summary task
 */
export const useStartTranscriptionSummary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      // NOTE: the generated body type is a plain object of form fields (the
      // hey-api `formDataBodySerializer` builds the multipart FormData itself
      // from `Object.entries(body)`); passing a real `FormData` instance here
      // — as this hook did before this type-only pass — means the serializer
      // sees no own-enumerable entries and sends an empty body. This hook has
      // no callers today, so the pre-existing behavior is unchanged; flagging
      // for a human to confirm before this is wired up to a real caller.
      const response =
        await transcribeAndSummarizeAudioApiV1TranscriptionSummarizePost({
          body: formData as unknown as BodyTranscribeAndSummarizeAudioApiV1TranscriptionSummarizePost,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage(
            "Failed to start transcription summary",
            response.error
          )
        );
      }

      return response.data;
    },
    onSuccess: (_data) => {
      // Invalidate tasks so any task list UI refreshes
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.TASKS] });

      // Also refresh assessments cache if needed
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.ASSESSMENTS] });
    },
    onError: (err) => {
      console.error("Error starting transcription summary:", err);
    },
  });
};

export default useStartTranscriptionSummary;
